import {
    EmptyStateIcons,
    FilterChip,
    SortDirection,
    Table,
    TableColumn,
    TwoLinesCell,
    Icons,
    Button,
    ButtonStyles,
    ButtonSizes,
    Tooltip
} from '@silverfort/sf-components';
import { SmartPolicyAction, SmartPolicyWithEntitiesFullData } from './SmartPoliciesTypes';
import { useSmartPolicyContext } from './SmartPolicyContext';
import { DateFormat, formatDate } from '../../../../../utils/dates';
import { bigNumberToText } from '../../../../../components/utils';
import { RISK_RULES } from '@silverfort-nodelibs/common/utils/insight';
import useHasPermissions from '../../../../../hooks/useHasPermissions';
import { Pages, Roles } from '../../../../../context/contexts/UserContext/permissionsTypes';
import { useMemo } from 'react';

const IdAndDeleteColumnsRender = (policy: SmartPolicyWithEntitiesFullData) => {
    const { setPolicyToDelete } = useSmartPolicyContext();

    return (
        <TwoLinesCell
            topText={String(policy.id)}
            bottomText=""
            rightElement={
                <div className={'invisible group-hover:visible'}>
                    <Button
                        buttonStyle={ButtonStyles.GhostSolid}
                        iconRight={Icons.Delete}
                        buttonSize={ButtonSizes.Medium}
                        onClick={(e) => {
                            e.stopPropagation();
                            setPolicyToDelete(policy);
                        }}
                    />
                </div>
            }
        />
    );
};

const DateColumn = ({ date }: { date: Date }) => {
    return (
        <div>
            <Tooltip title={formatDate(date, DateFormat.LONG_DATE_MD, true)}>
                <span>{formatDate(date, DateFormat.SHORT_DATE_MD, true)}</span>
            </Tooltip>
        </div>
    );
};

const TABLE_COLUMNS: TableColumn<SmartPolicyWithEntitiesFullData>[] = [
    {
        columnKey: 'name',
        columnClassName: 'min-w-[180px]',
        title: 'Name'
    },
    {
        columnKey: 'in_scope_sa',
        columnClassName: 'min-w-[150px]',
        title: 'Accounts in scope',
        render: ({ in_scope_sa }: SmartPolicyWithEntitiesFullData) => {
            return <>{bigNumberToText(in_scope_sa)}</>;
        }
    },
    {
        columnClassName: 'min-w-[250px]',
        columnKey: 'entities',
        title: 'Scope definition',
        render: ({ entities }: { entities: SmartPolicyWithEntitiesFullData['entities'] }) => {
            const includedEntities = entities.filter((entity) => !entity.excluded);
            if (includedEntities.length > 1) {
                const firstEntity = includedEntities[0];
                return (
                    <div className="flex gap-1">
                        <FilterChip className="!h-6" text={firstEntity.display_name} onClick={() => {}} />
                        <FilterChip className="!h-6" text={`+${includedEntities.length - 1}`} onClick={() => {}} />
                    </div>
                );
            } else if (includedEntities.length === 1) {
                const firstEntity = includedEntities[0];
                return <FilterChip className="!h-6" text={firstEntity.display_name} onClick={() => {}} />;
            }
            // If there are no entities, return an empty div or a message
            return <></>;
        },
        disableSort: true
    },
    {
        columnClassName: 'min-w-[400px]',
        columnKey: 'alter_after',
        title: 'Policies',
        render: ({
            alter_after,
            block_after,
            activated_action,
            send_to_siem,
            protect_risk_indicators,
            is_limit_activities,
            days_limit_activities
        }: SmartPolicyWithEntitiesFullData) => {
            const isDenyEnabled = activated_action.includes(SmartPolicyAction.BLOCK_AFTER);
            const isAlterEnabled = activated_action.includes(SmartPolicyAction.ALTER_AFTER);
            const isInteractiveLogin = protect_risk_indicators.includes(RISK_RULES.SERVICE_ACCOUNT_INTERACTIVE_LOGIN);

            const chips: { text: string; className?: string }[] = [
                {
                    text: `Notify: ${alter_after ? `${alter_after} days` : 'Immediately'}`,
                    className: '!h-6'
                },
                {
                    text: `Deny: ${block_after ? `${block_after} days` : 'Immediately'}`,
                    className: '!h-6'
                }
            ];
            if (send_to_siem) {
                chips.push({
                    text: 'SIEM'
                });
            }
            if (isInteractiveLogin) {
                chips.push({
                    text: 'Interactive Login'
                });
            }
            if (is_limit_activities) {
                chips.push({
                    text: `Resources: ${days_limit_activities} days`
                });
            }

            const visibleItems = chips.slice(0, 2);
            const hiddenItems = chips.slice(2);

            return (
                <div className="flex gap-2 w-full">
                    <div className="flex grow-1 gap-2">
                        {visibleItems.map((item) => (
                            <FilterChip className="h-6" {...item} onClick={() => {}} />
                        ))}
                    </div>
                    {hiddenItems.length > 0 && (
                        <Tooltip
                            placement="top-start"
                            title={
                                <div className="flex flex-col hover:pointer-events-none cursor-default">
                                    {hiddenItems.map((n, i) => (
                                        <div key={i}>{n.text}</div>
                                    ))}
                                </div>
                            }>
                            <FilterChip
                                className="h-6 overflow-hidden"
                                text={`+${hiddenItems.length}`}
                                onClick={() => {}}
                            />
                        </Tooltip>
                    )}
                </div>
            );
        },
        disableSort: true
    },
    {
        columnKey: 'update_time',
        title: 'Last update',
        columnClassName: 'min-w-[100px]',
        render: ({ update_time, created_time }: SmartPolicyWithEntitiesFullData) => {
            return <DateColumn date={update_time || created_time} />;
        }
    },
    {
        columnKey: 'last_run',
        columnClassName: 'min-w-[100px]',
        title: 'Last run',
        render: ({ last_run }: SmartPolicyWithEntitiesFullData) => {
            return last_run ? <DateColumn date={last_run} /> : 'N/A';
        },
        disableSort: true
    }
];

const ID_AND_DELETE_COLUMN: TableColumn<SmartPolicyWithEntitiesFullData> = {
    columnKey: 'id',
    title: 'ID',
    columnClassName: 'min-w-[72px]',
    render: IdAndDeleteColumnsRender,
    disableSort: true
};

const SmartPoliciesTable = ({ height }: { height?: number }) => {
    const { smartPolicies, loadingSmartPolicies, setOrderState, setSmartPolicyForEdit } = useSmartPolicyContext();
    const isEditor = useHasPermissions({ permission: Pages.SERVICE_ACCOUNTS, role: Roles.EDITOR });

    const tableColumns: TableColumn<SmartPolicyWithEntitiesFullData>[] = useMemo(() => {
        const copyIdAndDeleteColumn: TableColumn<SmartPolicyWithEntitiesFullData> = {
            ...ID_AND_DELETE_COLUMN
        } as TableColumn<SmartPolicyWithEntitiesFullData>;
        if (isEditor) {
            return [...TABLE_COLUMNS, copyIdAndDeleteColumn];
        }
        delete copyIdAndDeleteColumn.render;
        return [...TABLE_COLUMNS, copyIdAndDeleteColumn];
    }, [isEditor]);

    return (
        <Table
            bodyHeight={height ? height - 58 : 0}
            loading={loadingSmartPolicies}
            columns={tableColumns}
            defaultSortColumn="name"
            defaultSortDirection="desc"
            rowHeight={64}
            rowMargin={8}
            rowClassName={`rounded-lg mb-4 border`}
            rows={smartPolicies}
            onSortChange={(columnKey: keyof SmartPolicyWithEntitiesFullData, sortDirection: SortDirection) => {
                setOrderState({ orderByField: columnKey, orderDirection: sortDirection });
            }}
            onRowClick={(row) => {
                setSmartPolicyForEdit(row);
            }}
            EmptyStateProps={{
                icon: EmptyStateIcons.NoItems,
                title: 'No Policy Automations',
                description: 'Select Add to automate the setup of virtual fencing policies.'
            }}
        />
    );
};

export default SmartPoliciesTable;
